import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { EnrichmentRequest } from "../api/types";
import { REQUEST_WORDS, expectedWords } from "../lib/enrichment";
import { campaignPath, gapGroups, stageWord, useGapWords } from "../lib/campaign";
import { relativeDate, shortDate } from "../lib/format";
import { Spinner } from "./Spinner";

/* The enricher's queue: every campaign somebody asked to have worked by
 * hand, from api.enrichment_request (engine migration 157).
 *
 * One row per request, open ones first, in the engine's order of the
 * enrichment work (enrichment_rank, engine migration 313): campaigns needed
 * now, then by estimated start, then by place in a sequence and the people
 * to call ahead, so the enricher can work ahead. Under the campaign's name,
 * the engine's sentence about its estimated start, or its sentence about
 * why it is where it is when it has no date (engine migrations 256, 313). Each says who asked and when, what
 * the campaign needs in the engine's own words ("214 contacts need a
 * mobile"), what has been added since the request, and what is running for
 * it with an expected finish. Under the needs, the areas for further
 * enrichment, the same counts as the campaign's Enrichment tab
 * (api.campaign_progress.gaps), one line a kind, so whoever takes a request
 * sees where more data would help without opening the campaign (James,
 * 2026-10-08). The enricher takes a request here, works it from the
 * campaign's page (downloads, Apollo, edits), and marks it complete here or
 * there.
 *
 * Until 2026-10-23 the newest notices of requests were listed under the
 * table, each ending "Shown here, not emailed yet", from before EVIE could
 * send a notice anywhere (engine migration 157). Notices post to Slack since
 * engine migration 271, and the table already says who asked and when. */
export function EnrichmentRequests({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const [showDone, setShowDone] = useState(false);
  const [completing, setCompleting] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [said, setSaid] = useState<string | null>(null);

  const requests = useQuery({
    queryKey: ["enrichment-requests", showDone],
    refetchInterval: 60_000,
    queryFn: async () => {
      let q = apiClient(token).from("enrichment_request")
        .select("id,name,label,status,created_by,auto_enrich,manual_status,manual_requested_at,manual_requested_by,manual_assigned_to,manual_started_at,manual_completed_at,manual_completed_by,manual_note,roles,contact_state,research,measured_at,properties,people,people_with_mobile,needs_work,needs_words,properties_since_request,mobiles_since_request,people_since_request,enrich_job_status,enrich_batch,web_members,web_waiting,web_expected_done_at,apollo_jobs_open,call_on,calling_words,calling_warning,enrichment_rank,enrichment_words");
      if (!showDone) q = q.in("manual_status", ["requested", "in_progress"]);
      // The engine's order of the work, then the newest request (engine
      // migration 313). A campaign at Calling or done has no rank and comes last.
      const { data, error } = await q.order("enrichment_rank", { ascending: true, nullsFirst: false })
        .order("manual_requested_at", { ascending: false }).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EnrichmentRequest[];
    },
  });

  const names = (requests.data ?? []).map((r) => r.name ?? "").filter(Boolean);
  const gaps = useQuery({
    queryKey: ["enrichment-request-gaps", names.join(",")],
    enabled: names.length > 0,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_progress")
        .select("criteria_set_name,gaps").in("criteria_set_name", names).limit(names.length);
      if (error) throw new Error(apiErrorMessage(error));
      return new Map(((data ?? []) as { criteria_set_name: string | null; gaps: unknown }[])
        .map((g) => [g.criteria_set_name ?? "", g.gaps]));
    },
  });
  const gapWords = useGapWords(token);

  const act = useMutation({
    mutationFn: async (what: { do: "start" | "complete"; name: string; note?: string }) =>
      what.do === "start"
        ? callFunction(token, "start_enrichment", { p_name: what.name })
        : callFunction(token, "complete_enrichment", { p_name: what.name, p_note: what.note ?? null }),
    onSuccess: () => {
      setSaid(null); setCompleting(null); setNote("");
      queryClient.invalidateQueries({ queryKey: ["enrichment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const rows = requests.data ?? [];
  return (
    <section>
      <div className="bar">
        <h2 style={{ margin: 0 }}>Requests</h2>
        <label className="check">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          {" "}show completed
        </label>
      </div>
      {requests.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : requests.isError ? <p className="refusal">{(requests.error as Error).message}</p>
        : !rows.length ? (
          <p className="muted">
            {showDone ? "No campaign has requested manual enrichment yet." : "No open requests. A campaign arrives here when its manual enrichment starts."}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Request</th>
                <th>Needs</th>
                <th>Since the request</th>
                <th>Running</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const name = r.name ?? "";
                const open = r.manual_status === "requested" || r.manual_status === "in_progress";
                return (
                  <tr key={r.id}>
                    <td>
                      <Link to={campaignPath(name, "enrichment")}>{r.label || name}</Link>
                      {r.calling_words ? <div className={r.calling_warning ? "refusal" : undefined}>{r.calling_words}</div>
                        : r.enrichment_words ? <div className="muted">{r.enrichment_words}</div> : null}
                      <div className="muted">{stageWord(r.status)}{r.auto_enrich ? ", automatic web enrichment" : ""}</div>
                      {r.roles?.length ? <div className="muted">Reach: {r.roles.join(", ")}{r.contact_state ? ` in ${r.contact_state}` : ""}</div> : null}
                    </td>
                    <td>
                      <div>{REQUEST_WORDS[r.manual_status ?? ""] ?? r.manual_status}</div>
                      <div className="muted">Asked by {r.manual_requested_by} {relativeDate(r.manual_requested_at)}</div>
                      {r.manual_status === "in_progress" ? <div className="muted">With {r.manual_assigned_to} since {shortDate(r.manual_started_at)}</div> : null}
                      {r.manual_status === "complete" ? <div className="muted">Completed by {r.manual_completed_by} {relativeDate(r.manual_completed_at)}{r.manual_note ? `: ${r.manual_note}` : ""}</div> : null}
                    </td>
                    <td>
                      {r.measured_at
                        ? <>{r.needs_words || <span className="muted">Nothing needs work.</span>}
                            <div className="muted">{(r.properties ?? 0).toLocaleString()} properties, {(r.people ?? 0).toLocaleString()} people, {(r.people_with_mobile ?? 0).toLocaleString()} with a mobile. Measured {relativeDate(r.measured_at)}.</div>
                            <GapLines groups={gaps.data?.has(name) && gapWords.data ? gapGroups(gaps.data.get(name), gapWords.data) : undefined}
                                      failed={gaps.isError || gapWords.isError} /></>
                        : <span className="muted">Not measured yet.</span>}
                    </td>
                    <td>
                      {r.properties_since_request === null || r.properties_since_request === undefined
                        ? <span className="muted">No measurement before the request to count from.</span>
                        : <>{signed(r.properties_since_request)} properties, {signed(r.people_since_request)} people, {signed(r.mobiles_since_request)} mobiles</>}
                    </td>
                    <td className="muted">
                      {r.enrich_job_status === "queued" || r.enrich_job_status === "running" ? <div>Automatic web enrichment {r.enrich_job_status}.</div> : null}
                      {r.web_members && r.web_waiting ? <div>Web enrichment: {r.web_waiting.toLocaleString()} of {r.web_members.toLocaleString()} waiting, {expectedWords(r.web_expected_done_at)}.</div> : null}
                      {r.apollo_jobs_open ? <div>Apollo enrichment running.</div> : null}
                      {!r.enrich_job_status && !r.web_waiting && !r.apollo_jobs_open ? "nothing" : null}
                    </td>
                    <td>
                      {r.manual_status === "requested" ? (
                        <button onClick={() => act.mutate({ do: "start", name })} disabled={act.isPending}>Take the request</button>
                      ) : null}
                      {open ? (
                        completing === name ? (
                          <div>
                            <label htmlFor={`done-${name}`} className="field-label">What did you find?</label>
                            <input id={`done-${name}`} value={note} onChange={(e) => setNote(e.target.value)} style={{ width: "14rem" }} />
                            <div className="bar">
                              <button onClick={() => act.mutate({ do: "complete", name, note })} disabled={act.isPending}>Complete</button>
                              <button onClick={() => { setCompleting(null); setNote(""); }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => { setCompleting(name); setNote(""); }} disabled={act.isPending}>Mark complete</button>
                        )
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      {said ? <p className="refusal" role="alert">{said}</p> : null}
    </section>
  );
}

/* The areas for further enrichment, one line a kind: "People: no mobile 34,
   from one source 12". Nothing while they load, and a sentence when they
   could not be read, so an empty cell never stands for no gaps. */
function GapLines({ groups, failed }: { groups: ReturnType<typeof gapGroups> | undefined; failed: boolean }) {
  if (failed) return <div className="refusal">The areas for further enrichment could not be read.</div>;
  if (!groups) return null;
  if (!groups.length) return <div className="muted">Every record has what enrichment looks for.</div>;
  return (
    <>
      <div className="field-label">Areas for further enrichment</div>
      <ul className="fact-lines">
        {groups.map((g) => (
          <li key={g.kind}>
            {g.label}: {g.rows.map((r) => `${r.words.charAt(0).toLowerCase()}${r.words.slice(1)} ${r.n.toLocaleString()}`).join(", ")}
          </li>
        ))}
      </ul>
    </>
  );
}

function signed(n: number | null | undefined): string {
  if (n === null || n === undefined) return "0";
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString();
}
